import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { PageLoading } from '@client/components/_ui/feedback/PageLoading';
import { useUser } from '@client/hooks/business/user/useUser';
import { useAuthStore } from '@client/stores/AuthStore';
import { safeSessionStorage } from '@client/utils/storage/safe-storage';
import { SYSTEM_ORG_IDS } from '@shared/constants';
import { generateHref } from '@shared/utils/core/navigation';
import { createFileRoute } from '@tanstack/react-router';
import { LogOut, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { match } from 'ts-pattern';

export const Route = createFileRoute('/org-switched')({
    component: OrgSwitched,
});

interface OrgSwitchInfo {
    fromOrgId: string;
    fromOrgName: string;
    toOrgId: string;
    timestamp: number;
}

function OrgSwitched() {
    const authStore = useAuthStore();
    const user = useUser({ throwOnUndefined: false });
    const [orgSwitchInfo, setOrgSwitchInfo] = useState<OrgSwitchInfo | null>(null);
    const [targetOrgName, setTargetOrgName] = useState<string>('');
    const [isNavigating, setIsNavigating] = useState(false);

    useEffect(() => {
        const info = safeSessionStorage.getItem('org-switch-info');
        if (info) {
            try {
                const parsed = JSON.parse(info);
                setOrgSwitchInfo(parsed);

                // Find the target org name
                if (user?.userOrgs) {
                    const targetOrg = user.userOrgs.find((org) => org.id === BigInt(parsed.toOrgId));
                    if (targetOrg) {
                        setTargetOrgName(targetOrg.name);
                    }
                }
            } catch (e) {
                console.error('Failed to parse org switch info', e);
            }
        }
    }, [user]);

    useEffect(() => {
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                setIsNavigating(false);
            }
        };

        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, []);

    const handleContinue = () => {
        setIsNavigating(true);

        // Resolve the target org from the stored switch info, falling back to the
        // persisted org (already updated to the new org by the tab that switched) so the
        // button still works when the entry is missing — e.g. after a page reload or a
        // previously failed attempt consumed it.
        let targetOrgId = authStore.org?.id;
        if (orgSwitchInfo) {
            try {
                const storedOrgId = BigInt(orgSwitchInfo.toOrgId);
                if (storedOrgId === SYSTEM_ORG_IDS.SYSTEM_CONSOLE || storedOrgId >= SYSTEM_ORG_IDS.FIRST_VALID_ORG_ID) {
                    targetOrgId = storedOrgId;
                }
            } catch {
                // Keep the persisted organisation when the stored ID is malformed.
            }
        }

        safeSessionStorage.removeItem('org-switch-info');

        // Hard navigation on purpose: this route sits outside RequireAuth, so the tab has
        // no token-refresh machinery and its rehydrated token is often expired by the time
        // the user returns to it. An in-page changeOrg() would fail silently on a 401. A
        // full load lands in RequireAuth, which owns token acquisition (silent refresh or
        // login redirect) and reconciles the org from the URL.
        const href = match(targetOrgId)
            .with(undefined, () => generateHref({ baseUrl: window.location.origin, to: '/login' }))
            .with(SYSTEM_ORG_IDS.SYSTEM_CONSOLE, () =>
                generateHref({ baseUrl: window.location.origin, to: '/admin-console' }),
            )
            .otherwise((orgId) =>
                generateHref({ baseUrl: window.location.origin, to: '/console/$orgId', params: { orgId } }),
            );
        window.location.replace(href);
    };

    const handleLogout = async () => {
        safeSessionStorage.removeItem('org-switch-info');
        await authStore.logout();
    };

    return (
        <PageController route={Route}>
            <PageLoading>
                <div className="flex min-h-screen items-center justify-center bg-background">
                    <div className="w-full max-w-lg">
                        <div className="rounded-2xl bg-card p-8 shadow-xl">
                            {/* Icon */}
                            <div className="mb-6 flex justify-center">
                                <div className="rounded-full bg-info-100 p-4 dark:bg-info-900/30">
                                    <RefreshCw className="h-8 w-8 text-info-600" />
                                </div>
                            </div>

                            {/* Title */}
                            {/* curium:allow-heading — organisation-switched notice page, outside the app shell */}
                            <h1 className="mb-3 text-center font-semibold text-2xl text-foreground">
                                Organisation Changed
                            </h1>

                            {/* Description */}
                            <p className="mb-8 text-center text-muted-foreground">
                                {orgSwitchInfo && targetOrgName ? (
                                    <>
                                        Your organisation was changed from{' '}
                                        <span className="font-medium text-foreground">
                                            "{orgSwitchInfo.fromOrgName}"
                                        </span>{' '}
                                        to <span className="font-medium text-foreground">"{targetOrgName}"</span> in
                                        another browser tab.
                                    </>
                                ) : (
                                    'Your organisation was changed in another browser tab.'
                                )}
                            </p>

                            {/* Buttons */}
                            <div className="space-y-3">
                                <Button
                                    type="primary"
                                    icon={<RefreshCw />}
                                    onClick={handleContinue}
                                    loading={isNavigating}
                                    size="large"
                                    block
                                >
                                    Continue with New Organisation
                                </Button>
                                <Button
                                    type="secondary"
                                    icon={<LogOut />}
                                    onClick={handleLogout}
                                    disabled={isNavigating}
                                    size="large"
                                    block
                                >
                                    Log Out
                                </Button>
                            </div>

                            {/* Info Box */}
                            <div className="mt-8 rounded-xl border border-separator bg-muted p-5">
                                <div className="flex gap-3">
                                    <div className="flex-shrink-0">
                                        <div className="rounded-full bg-info-100 p-1 dark:bg-info-900/30">
                                            <svg
                                                className="h-5 w-5 text-info-600"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        {/* curium:allow-heading — organisation-switched notice page, outside the app shell */}
                                        <h3 className="mb-1 font-semibold text-foreground text-sm">
                                            Why am I seeing this?
                                        </h3>
                                        <p className="text-muted-foreground text-sm leading-relaxed">
                                            When you switch organisations in one browser tab, we need to ensure all your
                                            other tabs are synchronized for security reasons. This prevents data from
                                            different organisations from being mixed.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <p className="mt-6 text-center text-sm text-subtle-foreground">
                            Need help?{' '}
                            <a
                                href="mailto:support@getcurium.com"
                                className="font-medium text-accent-500 hover:text-accent-600"
                            >
                                Contact support
                            </a>
                        </p>
                    </div>
                </div>
            </PageLoading>
        </PageController>
    );
}
